import React, { forwardRef, useState, useMemo, useCallback, useEffect } from 'react'
import { Input } from 'reactstrap'
import GlobalCustomTable from '../../../GlobalCustomTable';

const ApplicableHeaderCell = ({ onChange }) => {
    const [checked, setChecked] = useState(false);

    return (
        <div className="d-flex align-items-center">
            <div className="form-check mb-0 d-flex align-items-center">
                <Input
                    type="checkbox"
                    className="form-check-input"
                    checked={checked}
                    onChange={(event) => {
                        const value = event.target.checked;
                        setChecked(value);
                        onChange(value);
                    }}
                    id="applicableAllCheckbox"
                />

                <label
                    className="form-check-label ms-2 mt-lg-1"
                    htmlFor="applicableAllCheckbox"
                >
                    Aplicable
                </label>
            </div>
        </div>
    );
};


const NotApplicableHeaderCell = ({ onChange }) => {
    const [checked, setChecked] = useState(false);

    return (
        <div className="d-flex align-items-center">
            <div className="form-check mb-0 d-flex align-items-center">
                <Input
                    type="checkbox"
                    className="form-check-input"
                    checked={checked}
                    onChange={(event) => {
                        const value = event.target.checked;
                        setChecked(value);
                        onChange(value);
                    }}
                    id="notApplicableAllCheckbox"
                />

                <label
                    className="form-check-label ms-2 mt-lg-1"
                    htmlFor="notApplicableAllCheckbox"
                >
                    Not Aplicable
                </label>
            </div>
        </div>
    );
};


const EffectiveHeaderCell = ({ onChange }) => {
    const [checked, setChecked] = useState(false);

    return (
        <div className="d-flex align-items-center">
            <div className="form-check mb-0 d-flex align-items-center">
                <Input
                    type="checkbox"
                    className="form-check-input"
                    checked={checked}
                    onChange={(event) => {
                        const value = event.target.checked;
                        setChecked(value);
                        onChange(value);
                    }}
                    id="effectiveAllCheckbox"
                />

                <label
                    className="form-check-label ms-2 mt-lg-1"
                    htmlFor="effectiveAllCheckbox"
                >
                    Effective
                </label>
            </div>
        </div>
    );
};

// Isolated header cell — owns its own local input/type state
// so changing the Discount header does not recreate the columns
// and reset pagination back to page 1.
const DiscountHeaderCell = ({ onValueChange, onTypeChange, disabled, percentDisabled }) => {
    const [local, setLocal] = useState({
        value: "",
        type: "%"
    });

    // Mirrors the child row's reset: when nothing is effective anymore,
    // snap the header back to % (and clear the value), same as
    // row["DiscountType"] = "%" / row["DiscountValue"] = 0 does per row.
    useEffect(() => {
        if (disabled) {
            setLocal({ value: "", type: "%" });
            onTypeChange("%");
            onValueChange("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabled]);

    return (
        <div
            className="d-flex flex-column gap-1"
            style={{ minWidth: 210 }}
        >
            <label className="form-check-label fw-semibold mb-0">
                Discount
            </label>

            <div
                className="d-flex align-items-stretch"
                style={{
                    border: "1px solid #ced4da",
                    borderRadius: "0.25rem",
                    overflow: "hidden",
                    background: "#fff",
                    minWidth: 0
                }}
            >
                <Input
                    type="text"
                    placeholder="Enter Value"
                    autoComplete="off"
                    className="font-size-12 border-0"
                    disabled={disabled}
                    style={{
                        flex: "1 1 100px",
                        minWidth: 80,
                        boxShadow: "none",
                        borderRadius: 0
                    }}
                    value={local.value}
                    onChange={(event) => {
                        const value = event.target.value;

                        setLocal(prev => ({
                            ...prev,
                            value
                        }));

                        onValueChange(value);
                    }}
                />

                <div
                    className="d-flex flex-shrink-0"
                    role="group"
                    aria-label="Header value type"
                    style={{ borderLeft: "1px solid #ced4da" }}
                >
                    <button
                        type="button"
                        disabled={disabled}
                        className={`btn btn-sm font-size-12 rounded-0 ${local.type === "Rs" ? "btn-secondary" : "btn-light"}`}
                        style={{ border: "none" }}
                        onClick={() => {
                            setLocal(prev => ({
                                ...prev,
                                type: "Rs"
                            }));

                            onTypeChange("Rs");
                        }}
                    >
                        ₹
                    </button>

                    <button
                        type="button"
                        disabled={percentDisabled}
                        className={`btn btn-sm font-size-12 rounded-0 ${local.type === "%" ? "btn-secondary" : "btn-light"}`}
                        style={{ border: "none", borderLeft: "1px solid #ced4da" }}
                        onClick={() => {
                            setLocal(prev => ({
                                ...prev,
                                type: "%"
                            }));

                            onTypeChange("%");
                        }}
                    >
                        %
                    </button>
                </div>
            </div>
        </div>
    );
};

const SchemeItemTabForm = forwardRef(({ props, setState, state }, ref) => {

    // Generic, immutable single-row updater.
    // Matches on the table's keyField ("value") and merges the given changes.
    const updateRow = useCallback((rowKey, changes) => {
        setState(prev => prev.map(row =>
            row.value === rowKey ? { ...row, ...changes } : row
        ));
    }, [setState]);

    // ONLY called from the Discount header controls. Applies to every row.
    const applyDiscountValueToAll = useCallback((value) => {
        setState(prev => prev.map(row => ({ ...row, DiscountValue: value })));
    }, [setState]);

    // ONLY called from the Discount header controls. Applies to every row.
    const applyDiscountTypeToAll = useCallback((type) => {
        setState(prev => prev.map(row => ({ ...row, DiscountType: type })));
    }, [setState]);

    const AplicableheaderCheckboxChange = useCallback((isAplicable) => {
        setState(prev =>
            prev.map(row => ({
                ...row,
                applicable: isAplicable,
                not_applicable: false
            }))
        );
    }, [setState]);


    const Not_AplicableheaderCheckboxChange = useCallback((isNotAplicable) => {
        setState(prev =>
            prev.map(row => ({
                ...row,
                not_applicable: isNotAplicable,
                applicable: false
            }))
        );
    }, [setState]);


    const effectiveheaderCheckboxChange = useCallback((isEffective) => {
        setState(prev =>
            prev.map(row => ({
                ...row,
                effective: isEffective
            }))
        );
    }, [setState]);

    // Mirrors the same enable/disable rule the row-level Discount
    // control uses (row.effective), but aggregated: header stays
    // enabled if AT LEAST ONE row is effective.
    const anyRowEffective = useMemo(
        () => state.some(row => row.effective),
        [state]
    );

    const columns = useMemo(() => [
        {
            dataField: 'ItemName',
            text: 'Item Name',

        },
        {
            dataField: 'applicable',
            text: 'Aplicable',
            headerFormatter: () => (
                <ApplicableHeaderCell
                    onChange={AplicableheaderCheckboxChange}
                />
            ),
            formatter: (cell, row, rowIndex, state) => {

                return (
                    <Input
                        type="checkbox"
                        checked={row.applicable && !(row.not_applicable)}
                        onChange={(event) => {
                            const checked = event.target.checked;
                            updateRow(row.value, {
                                applicable: checked,
                                not_applicable: checked ? false : row.not_applicable
                            });
                        }}
                    />
                )
            }
        },
        {
            dataField: 'not_applicable',
            text: 'Not Aplicable',

            headerFormatter: () => (
                <NotApplicableHeaderCell
                    onChange={Not_AplicableheaderCheckboxChange}
                />
            ),
            formatter: (cell, row, rowIndex) => {

                return (
                    <Input
                        type="checkbox"
                        checked={row.not_applicable && !(row.applicable)}
                        onChange={(event) => {
                            const checked = event.target.checked;
                            updateRow(row.value, {
                                not_applicable: checked,
                                applicable: checked ? false : row.applicable
                            });
                        }}
                    />
                )
            }
        },
        {
            dataField: 'effective',
            text: 'Effective',

            headerFormatter: () => (
                <EffectiveHeaderCell
                    onChange={effectiveheaderCheckboxChange}
                />
            ),
            formatter: (cell, row, rowIndex) => (
                <Input
                    type="checkbox"
                    checked={row.effective}
                    onChange={(event) => {
                        updateRow(row.value, { effective: event.target.checked });
                    }}
                />
            )
        },
        {

            dataField: '',
            text: 'Quantity',
            // hidden: SchemeItemTab.Quantityhidden,

            formatter: (_, row) => {

                if (!(row.effective)) {
                    row["Quantity"] = 0
                }
                return (
                    <Input
                        name="Quantity"
                        id={`Quantity`}
                        value={row.Quantity || ""}
                        type="text"
                        // disabled={!(row.effective)}
                        disabled={true}
                        placeholder="Enter Quantity"
                        autoComplete="off"
                        className="font-size-12 mt-2"
                        style={{ maxWidth: "150px" }}
                        onChange={(event) => {
                            updateRow(row.value, { Quantity: event.target.value });
                        }}
                    />

                )
            },
        },

        {
            dataField: 'label',
            text: 'Discount',
            // hidden: SchemeItemTab.Discounthidden,

            // Discount header — the ONLY control that applies a value/type
            // to every row at once. Row-level edits never reach this state
            // or affect other rows.
            headerFormatter: () => (
                <DiscountHeaderCell
                    onValueChange={applyDiscountValueToAll}
                    onTypeChange={applyDiscountTypeToAll}
                    disabled={!anyRowEffective}
                    percentDisabled={anyRowEffective}
                />
            ),

            // Row-level: fully independent per row again, exactly like the
            // original — editing one row's value/₹/% only ever updates
            // that row. Styling matches the header so the column reads
            // as one consistent control, and wraps gracefully on narrow
            // screens instead of overflowing.
            formatter: (_, row, rowIndex) => {
                if (!(row.effective)) {
                    row["DiscountValue"] = 0
                    row["DiscountType"] = "%"
                }
                // 👇 helper to move focus up/down between discount inputs
                const focusDiscountInput = (direction) => {
                    const inputs = Array.from(
                        document.querySelectorAll('input[id^="discount-value-"]')
                    );

                    const currentIndex = inputs.indexOf(document.activeElement);

                    if (currentIndex === -1) return;

                    const targetIndex =
                        direction === "down"
                            ? currentIndex + 1
                            : currentIndex - 1;

                    if (targetIndex >= 0 && targetIndex < inputs.length) {
                        inputs[targetIndex].focus();
                        inputs[targetIndex].select?.();
                    }
                };
                const handleKeyDown = (event) => {
                    if (event.key === "ArrowDown") {
                        event.preventDefault();
                        focusDiscountInput("down");
                    } else if (event.key === "ArrowUp") {
                        event.preventDefault();
                        focusDiscountInput("up");
                    }
                };
                return (
                    <div
                        className="d-flex align-items-center flex-wrap gap-2"
                        style={{ minWidth: 210, zIndex: 0 }}
                    >
                        <Input
                            name="DiscountValue"
                            id={`discount-value-${row.ItemID}`}
                            value={row.DiscountValue || ""}
                            type="text"
                            disabled={!(row.effective)}
                            placeholder="Enter Value"
                            autoComplete="off"
                            className="font-size-12"
                            style={{ flex: "1 1 100px", minWidth: 90 }}
                            onChange={(event) => {
                                updateRow(row.value, { DiscountValue: event.target.value });
                            }}
                            onKeyDown={handleKeyDown}
                        />
                        <div
                            className="btn-group btn-group-sm flex-shrink-0"
                            role="group"
                            aria-label="Value type"
                        >
                            <input
                                type="checkbox"
                                id={`discount-rs-${row.ItemID}`}
                                className="btn-check"
                                autoComplete="off"
                                disabled={!(row.effective)}
                                checked={row.DiscountType === "Rs"}
                                onChange={() => {
                                    updateRow(row.value, { DiscountType: "Rs" });
                                }}
                            />
                            <label
                                className="btn btn-outline-secondary px-2"
                                htmlFor={`discount-rs-${row.ItemID}`}
                                style={{ zIndex: 0 }}
                            >
                                ₹
                            </label>

                            <input
                                type="checkbox"
                                id={`discount-percent-${row.ItemID}`}
                                className="btn-check"
                                autoComplete="off"
                                disabled={row.effective}
                                checked={row.DiscountType === "%"}
                                onChange={() => {
                                    updateRow(row.value, { DiscountType: "%" });
                                }}
                            />
                            <label
                                className="btn btn-outline-secondary px-2"
                                htmlFor={`discount-percent-${row.ItemID}`}
                                style={{ zIndex: 0 }}
                            >
                                %
                            </label>
                        </div>
                    </div>
                )
            },
        },

    ], [
        updateRow,
        applyDiscountValueToAll,
        applyDiscountTypeToAll,
        AplicableheaderCheckboxChange,
        Not_AplicableheaderCheckboxChange,
        effectiveheaderCheckboxChange,
        anyRowEffective
    ]);
    const columnsRef = React.useRef(columns);
    if (columnsRef.current !== columns) {
        console.log('[SchemeItemTabForm] columns reference CHANGED');
        columnsRef.current = columns;
    } else {
        console.log('[SchemeItemTabForm] columns reference SAME (good)');
    }
    return (
        <>
            <GlobalCustomTable
                keyField="value"
                data={state}
                columns={columns}
                paginationEnabled={25}
                stablePagination={true}
                controlledPagination={false}
                id="table_Arrow"
                noDataIndication={
                    <div className="text-danger text-center">
                        Items Not available
                    </div>
                }
            />
        </>

    )
})

export default SchemeItemTabForm;